import { cn } from '@rematter/ui-web';

import { ConfirmationDialog } from './ConfirmationDialog';

type RoleActionDialogRole = {
  id: string;
  name: string;
};

export type RoleActionDialogState =
  | { type: 'closed' }
  | { type: 'archive'; role: RoleActionDialogRole }
  | { type: 'restore'; role: RoleActionDialogRole }
  | { type: 'bulkArchive'; roleIds: string[] }
  | { type: 'bulkRestore'; roleIds: string[] };

type SingleRoleTranslation = {
  title: (name: string) => string;
  description: (name: string) => string;
  confirm: string;
  discard: string;
};

type BulkRoleTranslation = {
  title: (count: number) => string;
  description: (count: number) => string;
  confirm: string;
  discard: string;
};

export type RoleActionDialogTranslations = {
  archive: SingleRoleTranslation;
  restore: SingleRoleTranslation;
  bulkArchive: BulkRoleTranslation;
  bulkRestore: BulkRoleTranslation;
};

export type RoleActionConfirmationDialogProps = {
  dialogState: RoleActionDialogState;
  dialogs: RoleActionDialogTranslations;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onOpenChange: (open: boolean) => void;
  className?: string;
};

export const RoleActionConfirmationDialog = ({
  dialogState,
  dialogs,
  isPending,
  onConfirm,
  onCancel,
  onOpenChange,
  className,
}: RoleActionConfirmationDialogProps) => {
  if (dialogState.type === 'closed') return null;

  const dialogProps = getDialogProps(dialogState, dialogs);

  return (
    <ConfirmationDialog
      isOpen
      onOpenChange={onOpenChange}
      title={dialogProps.title}
      description={dialogProps.description}
      confirmLabel={dialogProps.confirmLabel}
      cancelLabel={dialogProps.cancelLabel}
      confirmColor={dialogProps.confirmColor}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isLoading={isPending}
      className={cn('@RoleActionConfirmationDialog', className)}
    />
  );
};

type DialogProps = {
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  confirmColor: 'failure' | 'primary';
};

function getDialogProps(
  state: Exclude<RoleActionDialogState, { type: 'closed' }>,
  dialogs: RoleActionDialogTranslations,
): DialogProps {
  switch (state.type) {
    case 'archive':
      return {
        title: dialogs.archive.title(state.role.name),
        description: dialogs.archive.description(state.role.name),
        confirmLabel: dialogs.archive.confirm,
        cancelLabel: dialogs.archive.discard,
        confirmColor: 'failure',
      };
    case 'restore':
      return {
        title: dialogs.restore.title(state.role.name),
        description: dialogs.restore.description(state.role.name),
        confirmLabel: dialogs.restore.confirm,
        cancelLabel: dialogs.restore.discard,
        confirmColor: 'primary',
      };
    case 'bulkArchive':
      return {
        title: dialogs.bulkArchive.title(state.roleIds.length),
        description: dialogs.bulkArchive.description(state.roleIds.length),
        confirmLabel: dialogs.bulkArchive.confirm,
        cancelLabel: dialogs.bulkArchive.discard,
        confirmColor: 'failure',
      };
    case 'bulkRestore':
      return {
        title: dialogs.bulkRestore.title(state.roleIds.length),
        description: dialogs.bulkRestore.description(state.roleIds.length),
        confirmLabel: dialogs.bulkRestore.confirm,
        cancelLabel: dialogs.bulkRestore.discard,
        confirmColor: 'primary',
      };
  }
}
